// ===========================================
// PROVIDER: Notificaciones con Real-time (CORE)
// ===========================================

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  lazy,
  Suspense,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useSupabase } from '@/lib/supabase-context'
import { notificacionesService } from '../services/notificacionesService'
import type { Notificacion, ResumenNotificaciones, TipoNotificacion } from '../types/notificaciones'
import { usePreferenciasNotificaciones, esCanalActivo } from '@/core/hooks/usePreferenciasNotificaciones'
import { notificacionesKeys } from '@/core/hooks/useNotificaciones'
import { toast } from 'sonner'
import { playNotificationSound } from '@/lib/notification-sound'
import { normalizarEnlaceNotificacion } from '@/core/components/notificaciones/enlace-notificacion'

const PushPermissionPromptComponent = lazy(() =>
  import('@/components/notificaciones/PushPermissionPrompt').then((m) => ({ default: m.PushPermissionPrompt }))
)

function PushPermissionPromptLazy({ show }: { show: boolean }) {
  if (!show) return null
  return (
    <Suspense fallback={null}>
      <PushPermissionPromptComponent show={show} />
    </Suspense>
  )
}

// ===========================================
// Tipos del Contexto
// ===========================================

interface NotificacionesContextValue {
  // Estado
  notificaciones: Notificacion[]
  noLeidas: Notificacion[]
  conteoNoLeidas: number
  resumen: ResumenNotificaciones | null
  isLoading: boolean
  error: string | null
  showPushPrompt: boolean

  // Acciones
  refrescar: () => Promise<void>
  marcarLeida: (id: string) => Promise<void>
  marcarTodasLeidas: () => Promise<void>
  eliminar: (id: string) => Promise<void>
}

const NotificacionesContext = createContext<NotificacionesContextValue | null>(null)

// ===========================================
// Hook para consumir el contexto
// ===========================================

export function useNotificacionesContext() {
  const context = useContext(NotificacionesContext)
  if (!context) {
    throw new Error('useNotificacionesContext debe usarse dentro de NotificacionesProvider')
  }
  return context
}

// ===========================================
// Provider
// ===========================================

interface NotificacionesProviderProps {
  children: ReactNode
  /** Mostrar toast cuando llega una nueva notificación */
  mostrarToastNuevas?: boolean
  /** Límite de notificaciones a cargar */
  limite?: number
}

export function NotificacionesProvider({
  children,
  mostrarToastNuevas = true,
  limite = 50,
}: NotificacionesProviderProps) {
  const { user, isAuthenticated } = useAuth0()
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()
  const userEmail = user?.email

  // Preferencias para filtrado in-app
  const { data: preferencias } = usePreferenciasNotificaciones()
  const preferenciasRef = useRef(preferencias)
  useEffect(() => {
    preferenciasRef.current = preferencias
  }, [preferencias])

  // Estado
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([])
  const [resumen, setResumen] = useState<ResumenNotificaciones | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showPushPrompt, setShowPushPrompt] = useState(false)

  // Derivados
  const noLeidas = useMemo(
    () => notificaciones.filter((n) => !n.leida),
    [notificaciones]
  )

  const conteoNoLeidas = useMemo(() => noLeidas.length, [noLeidas])

  // ===========================================
  // Cargar notificaciones
  // ===========================================

  const cargarNotificaciones = useCallback(async () => {
    if (!userEmail || !supabase) return

    try {
      setIsLoading(true)
      setError(null)

      const [lista, resumenData] = await Promise.all([
        notificacionesService.listar(supabase, userEmail, { limite }),
        notificacionesService.obtenerResumen(supabase, userEmail),
      ])

      setNotificaciones(lista)
      setResumen(resumenData)
    } catch (err) {
      const mensaje = err instanceof Error ? err.message : 'Error al cargar notificaciones'
      setError(mensaje)
      console.error('Error cargando notificaciones:', err)
    } finally {
      setIsLoading(false)
    }
  }, [userEmail, supabase, limite])

  // ===========================================
  // Acciones
  // ===========================================

  const refrescar = useCallback(async () => {
    await cargarNotificaciones()
  }, [cargarNotificaciones])

  const marcarLeida = useCallback(
    async (id: string) => {
      if (!userEmail || !supabase) return

      try {
        await notificacionesService.marcarLeida(supabase, id)

        // Actualizar estado local
        setNotificaciones((prev) =>
          prev.map((n) => (n.id === id ? { ...n, leida: true } : n))
        )

        // Actualizar resumen
        setResumen((prev) =>
          prev
            ? { ...prev, noLeidas: Math.max(0, prev.noLeidas - 1) }
            : prev
        )
      } catch (err) {
        console.error('Error al marcar como leída:', err)
        toast.error('No se pudo marcar como leída')
      }
    },
    [userEmail, supabase]
  )

  const marcarTodasLeidas = useCallback(async () => {
    if (!userEmail || !supabase) return

    try {
      await notificacionesService.marcarTodasLeidas(supabase, userEmail)

      // Actualizar estado local
      setNotificaciones((prev) => prev.map((n) => ({ ...n, leida: true })))
      setResumen((prev) => (prev ? { ...prev, noLeidas: 0 } : prev))

      toast.success('Todas las notificaciones marcadas como leídas')
    } catch (err) {
      console.error('Error al marcar todas como leídas:', err)
      toast.error('No se pudieron marcar como leídas')
    }
  }, [userEmail, supabase])

  const eliminar = useCallback(
    async (id: string) => {
      if (!userEmail || !supabase) return

      try {
        const notif = notificaciones.find((n) => n.id === id)
        await notificacionesService.eliminar(supabase, id)

        // Actualizar estado local
        setNotificaciones((prev) => prev.filter((n) => n.id !== id))

        // Actualizar resumen
        if (notif && !notif.leida) {
          setResumen((prev) =>
            prev
              ? {
                  ...prev,
                  total: prev.total - 1,
                  noLeidas: Math.max(0, prev.noLeidas - 1),
                }
              : prev
          )
        } else {
          setResumen((prev) =>
            prev ? { ...prev, total: prev.total - 1 } : prev
          )
        }
      } catch (err) {
        console.error('Error al eliminar notificación:', err)
        toast.error('No se pudo eliminar la notificación')
      }
    },
    [userEmail, supabase, notificaciones]
  )

  // ===========================================
  // Suscripción Real-time
  // ===========================================

  useEffect(() => {
    if (!isAuthenticated || !userEmail || !supabase) {
      setIsLoading(false)
      return
    }

    // Cargar notificaciones iniciales
    cargarNotificaciones()

    // ORR-252: broadcast emitido por un trigger en la base, no postgres_changes.
    // Realtime conecta con la anon key sin los claims de Auth0 (supabase-context.tsx:155-159),
    // asi que postgres_changes no entrega nada: RLS lo filtra todo fila por fila.
    const channel = supabase
      .channel(`notificaciones:${userEmail}`)
      .on(
        'broadcast',
        { event: 'nueva' },
        async (payload) => {
          const { id } = payload.payload as { id?: string }
          if (!id) return

          // El canal es publico y solo transporta el id. El contenido se trae por
          // PostgREST, que si evalua RLS con la sesion de Auth0.
          const nuevaNotificacion = await notificacionesService.obtenerPorId(supabase, id)
          if (!nuevaNotificacion) return
          // Defensa en profundidad: al ser publico el canal, un tercero podria emitir un id ajeno.
          if (nuevaNotificacion.user_email !== userEmail) return

          // La campanita y la página leen por TanStack Query, no de este estado.
          queryClient.invalidateQueries({ queryKey: notificacionesKeys.all })

          // Check if in-app channel is active for this notification type
          const inAppActivo = esCanalActivo(
            preferenciasRef.current,
            nuevaNotificacion.tipo as TipoNotificacion,
            'in_app'
          )

          // Always add to list (for history); mark as read if in_app is disabled
          const notifToAdd = inAppActivo
            ? nuevaNotificacion
            : { ...nuevaNotificacion, leida: true }
          setNotificaciones((prev) => [notifToAdd, ...prev].slice(0, limite))

          if (inAppActivo) {
            // Actualizar resumen (unread count)
            setResumen((prev) =>
              prev
                ? {
                    ...prev,
                    total: prev.total + 1,
                    noLeidas: prev.noLeidas + 1,
                  }
                : prev
            )

            // Sonido + push prompt para tareas delegadas
            if (nuevaNotificacion.tipo === 'tarea') {
              playNotificationSound()
              setShowPushPrompt(true)
            }

            // Mostrar toast si está habilitado
            if (mostrarToastNuevas) {
              toast.info(nuevaNotificacion.titulo, {
                description: nuevaNotificacion.mensaje,
                action: nuevaNotificacion.enlace
                  ? {
                      label: 'Ver',
                      onClick: () => {
                        window.location.href = normalizarEnlaceNotificacion(
                          nuevaNotificacion.enlace!
                        )
                      },
                    }
                  : undefined,
              })
            }
          } else {
            // Still increment total but not unread
            setResumen((prev) =>
              prev
                ? { ...prev, total: prev.total + 1 }
                : prev
            )
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'CHANNEL_ERROR') {
          console.error('Notificaciones Realtime: channel error', err)
        } else if (status === 'TIMED_OUT') {
          console.error('Notificaciones Realtime: timed out — will auto-reconnect')
        }
      })

    // Cleanup
    return () => {
      supabase.removeChannel(channel)
    }
  }, [isAuthenticated, userEmail, supabase, queryClient, cargarNotificaciones, limite, mostrarToastNuevas])

  // ===========================================
  // Valor del Contexto
  // ===========================================

  const value = useMemo<NotificacionesContextValue>(
    () => ({
      notificaciones,
      noLeidas,
      conteoNoLeidas,
      resumen,
      isLoading,
      error,
      showPushPrompt,
      refrescar,
      marcarLeida,
      marcarTodasLeidas,
      eliminar,
    }),
    [
      notificaciones,
      noLeidas,
      conteoNoLeidas,
      resumen,
      isLoading,
      error,
      showPushPrompt,
      refrescar,
      marcarLeida,
      marcarTodasLeidas,
      eliminar,
    ]
  )

  return (
    <NotificacionesContext.Provider value={value}>
      {children}
      <PushPermissionPromptLazy show={showPushPrompt} />
    </NotificacionesContext.Provider>
  )
}

// ===========================================
// Export adicional para compatibilidad
// ===========================================

export { NotificacionesContext }
